import React from 'react';
import formatNumber from 'format-number';
import { format } from 'date-fns';
import { cs, signFile } from '../utils';
import { Checkbox } from './checkbox';
import { useNotionContext } from '../context';
import { Text } from './text';
import { evalFormula } from '../eval-formula';
import { PageTitle } from './page-title';
import { User } from './user';
import { LazyImage } from './lazy-image';
import { UseRetrieveBlocks } from '../hooks/useRetrieveBlocks';
/**
 * Renders a single value of structured Notion data according to its schema.
 *
 * This corresponds to rendering the content of a single cell in a table.
 * Property rendering is re-used across all the different types of collection views.
 */
export var Property = function (_a) {
    var _b, _c, _d;
    var schema = _a.schema, data = _a.data, block = _a.block, collection = _a.collection, _e = _a.inline, inline = _e === void 0 ? false : _e, property_style = _a.property_style, type = _a.type;
    var wrapAsBaseDecoration = function (value) { return [value]; };
    var _f = useNotionContext(), components = _f.components, mapImageUrl = _f.mapImageUrl, mapPageUrl = _f.mapPageUrl, recordMap = _f.recordMap;
    var retrieveMissingBlocks = UseRetrieveBlocks().retrieveMissingBlocks;
    var mystyle = property_style
        ? {
            position: property_style.position,
            right: property_style.right
        }
        : {};
    if (schema) {
        var content = null;
        var endDate = null;
        var flatDate = null;
        if (data ||
            schema.type === 'checkbox' ||
            schema.type === 'title' ||
            schema.type === 'formula' ||
            schema.type === 'created_by' ||
            schema.type === 'last_edited_by' ||
            schema.type === 'created_time' ||
            schema.type === 'last_edited_time' ||
            schema.type === 'status' ||
            schema.type === 'date') {
            switch (schema.type) {
                case 'relation':
                    // console.log('relation', schema, data)
                    content = React.createElement(Text, { value: data, block: block });
                    break;
                case 'formula':
                    // TODO
                    // console.log('formula', schema.formula, {
                    //   schema: collection?.schema,
                    //   properties: block?.properties
                    // })
                    try {
                        content = evalFormula(schema.formula, {
                            schema: collection === null || collection === void 0 ? void 0 : collection.schema,
                            properties: block === null || block === void 0 ? void 0 : block.properties
                        });
                        if (isNaN(content)) {
                            // console.log('NaN', schema.formula)
                        }
                        if (content instanceof Date) {
                            content = format(content, 'MMM d, YYY hh:mm aa');
                        }
                        if (typeof content === 'number') {
                            content = formatNumber()(content);
                        }
                    }
                    catch (err) {
                        // console.log('error evaluating formula', schema.formula, err)
                        content = null;
                    }
                    break;
                case 'title':
                    // console.log('title')
                    if (block) {
                        // console.log('there is block')
                        content = (React.createElement(components.pageLink, { className: cs('notion-page-link'), href: mapPageUrl(block.id) },
                            React.createElement(PageTitle, { block: block, type: type })));
                    }
                    else {
                        content = React.createElement(Text, { value: data, block: block });
                    }
                    break;
                case 'select':
                // intentional fallthrough
                case 'multi_select': {
                    var values = (data[0][0] || '').split(',');
                    content = values.map(function (value, index) {
                        var _a;
                        var option = (_a = schema.options) === null || _a === void 0 ? void 0 : _a.find(function (option) { return value === option.value; });
                        if (value == '')
                            return null;
                        var color = option === null || option === void 0 ? void 0 : option.color;
                        return (React.createElement("div", { style: mystyle, key: index, className: cs("notion-property-".concat(schema.type, "-item"), color && "notion-item-".concat(color), color && "notion-".concat(color, "_background")) }, value));
                    });
                    break;
                }
                case 'person':
                    content = React.createElement(User, { data: data, block: block });
                    break;
                case 'file': {
                    // TODO: assets should be previewable via image-zoom
                    var files = data
                        .filter(function (v) { return v.length === 2; })
                        .map(function (f) { return f.flat().flat(); });
                    var imageExt_1 = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
                    content = files.map(function (file, i) {
                        var ext = file[0].split('?')[0].split('.').pop();
                        if (imageExt_1.includes(ext)) {
                            var link = mapImageUrl(file[2], block);
                            var image = signFile(link, block);
                            return (React.createElement(components.link, { key: i, className: 'notion-property-file', href: link, target: '_blank', rel: 'noreferrer noopener' },
                                React.createElement(LazyImage, { alt: file[0], src: image })));
                        }
                        else {
                            return (React.createElement("a", { href: signFile(file[2], block.id), key: i, target: '_blank', rel: 'noreferrer', className: 'notion-property-file-name' },
                                React.createElement("span", null, file[0])));
                        }
                    });
                    break;
                }
                case 'checkbox': {
                    var isChecked = data && data[0][0] === 'Yes';
                    return (React.createElement("div", { className: 'notion-property-checkbox-container' },
                        React.createElement(Checkbox, { isChecked: isChecked, blockId: undefined }),
                        React.createElement("span", { className: 'notion-property-checkbox-text' }, schema.name)));
                }
                case 'url': {
                    // TODO: refactor to less hackyh solution
                    var d = JSON.parse(JSON.stringify(data));
                    if (inline) {
                        try {
                            var url = new URL(d[0][0]);
                            d[0][0] = url.hostname.replace(/^www\./, '');
                        }
                        catch (err) {
                            // ignore invalid urls
                        }
                    }
                    content = (React.createElement(Text, { value: d, block: block, inline: inline, linkProps: {
                            target: '_blank',
                            rel: 'noreferrer noopener'
                        } }));
                    break;
                }
                case 'email':
                    content = React.createElement(Text, { value: data, linkProtocol: 'mailto', block: block });
                    break;
                case 'phone_number':
                    content = React.createElement(Text, { value: data, linkProtocol: 'tel', block: block });
                    break;
                case 'number': {
                    var value = parseFloat(data[0][0] || '0');
                    var breakEarly = false;
                    var output = '';
                    if (isNaN(value)) {
                        content = React.createElement(Text, { value: data, block: block });
                    }
                    else {
                        switch (schema.number_format) {
                            case 'number_with_commas':
                                output = formatNumber()(value);
                                break;
                            case 'percent':
                                output = formatNumber({ suffix: '%', round: 1 })(value * 100);
                                break;
                            case 'dollar':
                                output = formatNumber({ prefix: '$', round: 2, padRight: 2 })(value);
                                break;
                            case 'euro':
                                output = formatNumber({ prefix: '€', round: 2, padRight: 2 })(value);
                                break;
                            case 'pound':
                                output = formatNumber({ prefix: '£', round: 2, padRight: 2 })(value);
                                break;
                            case 'yen':
                                output = formatNumber({ prefix: '¥', round: 0 })(value);
                                break;
                            case 'rupee':
                                output = formatNumber({ prefix: '₹', round: 2, padRight: 2 })(value);
                                break;
                            case 'won':
                                output = formatNumber({ prefix: '₩', round: 0 })(value);
                                break;
                            case 'yuan':
                                output = formatNumber({ prefix: 'CN¥', round: 2, padRight: 2 })(value);
                                break;
                            default:
                                content = React.createElement(Text, { value: data, block: block });
                                breakEarly = true;
                                break;
                        }
                        if (!breakEarly) {
                            content = React.createElement(Text, { value: [[output]], block: block });
                        }
                    }
                    break;
                }
                case 'created_time':
                    content = format(new Date((block === null || block === void 0 ? void 0 : block.created_time) || Date()), 'MMMM d, YYY hh:mm aa');
                    break;
                case 'last_edited_time': {
                    content = format(new Date((block === null || block === void 0 ? void 0 : block.last_edited_time) || Date()), 'MMM d, YYY hh:mm aa');
                    break;
                }
                case 'created_by': {
                    if (!data && !recordMap.notion_user[block.created_by_id]) {
                        retrieveMissingBlocks([
                            { id: block.created_by_id, table: 'notion_user' }
                        ]);
                    }
                    else {
                        content = React.createElement(User, { data: data, block: block });
                    }
                    break;
                }
                case 'last_edited_by':
                    break;
                case 'date':
                    flatDate = (_d = (_c = (_b = data === null || data === void 0 ? void 0 : data.flat()) === null || _b === void 0 ? void 0 : _b.flat()) === null || _c === void 0 ? void 0 : _c.flat()) === null || _d === void 0 ? void 0 : _d[2];
                    if ((flatDate === null || flatDate === void 0 ? void 0 : flatDate.start_date) && (flatDate === null || flatDate === void 0 ? void 0 : flatDate.start_time)) {
                        content =
                            format(new Date("".concat(flatDate.start_date, " ").concat(flatDate.start_time)), 'MMMM dd, yyyy HH:mm') + " (".concat((flatDate === null || flatDate === void 0 ? void 0 : flatDate.time_zone) || 'UTC', ")");
                        if ((flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_date) && (flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_time)) {
                            endDate =
                                format(new Date("".concat(flatDate.end_date, " ").concat(flatDate.end_time)), 'MMMM dd, yyyy HH:mm') + " (".concat((flatDate === null || flatDate === void 0 ? void 0 : flatDate.time_zone) || 'UTC', ")");
                        }
                        content =
                            (flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_date) !== undefined
                                ? "".concat(content, " \u2192 ").concat(endDate, " ")
                                : content;
                    }
                    else {
                        content = (flatDate === null || flatDate === void 0 ? void 0 : flatDate.start_date)
                            ? format(new Date("".concat(flatDate === null || flatDate === void 0 ? void 0 : flatDate.start_date)), 'yyyy/MM/d')
                            : null;
                        if (flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_date) {
                            endDate = (flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_date)
                                ? format(new Date("".concat(flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_date)), 'yyyy/MM/d')
                                : null;
                        }
                        content =
                            (flatDate === null || flatDate === void 0 ? void 0 : flatDate.end_date) !== undefined
                                ? "".concat(content, " \u2192 ").concat(endDate, " ")
                                : content;
                    }
                    break;
                case 'status':
                    if (data !== undefined) {
                        content = React.createElement(Text, { value: data, linkProtocol: 'mailto', block: block });
                    }
                    else {
                        var defaultOption = (schema === null || schema === void 0 ? void 0 : schema.defaultOption)
                            ? [wrapAsBaseDecoration(schema.defaultOption)]
                            : [wrapAsBaseDecoration(' ')];
                        content = (React.createElement(Text, { value: defaultOption, linkProtocol: 'mailto', block: block }));
                    }
                    break;
                default:
                    content = React.createElement(Text, { value: data, block: block });
                    break;
            }
        }
        if (content &&
            schema.type == 'formula' &&
            String(content).startsWith('https')) {
            content = (React.createElement(components.pageLink, { className: 'notion-link', href: content, target: '_blank', rel: 'noopener noreferrer' }, content));
        }
        return (React.createElement("span", { className: "notion-property notion-property-".concat(schema.type) }, content));
    }
    return null;
};
//# sourceMappingURL=property.js.map